// @givmo/payments — payment-processor config (config, not code).
//
// 03 §6 / Payment Processor Plan F5: per-env payment config — active processor, publishable
// key, card-page URLs, wallet ids — is served from a BACKEND endpoint precisely to kill the
// legacy 14-file hardcoded-key sprawl. This package consumes that endpoint; it NEVER bakes a
// publishable key, processor identity, or wallet merchant id into the monorepo.
//
// The endpoint is a trust boundary (its response drives wallet/Shift4 behavior), so the
// payload is validated with Zod here — types-only everywhere else, Zod at boundaries
// (03 §2). A malformed/partial payload fails loudly rather than silently mis-tokenizing.
//
// Today's known shape (donate-web's `GET /public/payments/config`) returns a bare
// `{ publishable_key }`. F5 broadens it to the full processor descriptor below; both the
// legacy bare key and the richer document parse, so a consumer is forward-compatible with
// the F5 rollout without a code change.
import { z } from 'zod';

// NF-WEB6: never ask the page to evaluate a string. Zod 4 probes `new Function("")` when the first
// object schema below is BUILT, to decide whether it may compile faster parsers; under the CSP every
// page loading this package serves (no 'unsafe-eval', PCI DSS 4.0 6.4.3) the browser refuses it and
// logs a `script-src` violation, though Zod then parses the ordinary way. Jitless mode skips the
// probe and always takes that ordinary path, so validation accepts and refuses exactly as before.
// It must run before the first schema, and it is global: it applies to every Zod schema built after
// it on the page, in each app that loads this package.
z.config({ jitless: true });

/** Publishable key shape — the only key form this package ever touches (never a secret). */
const publishableKeySchema = z
  .string()
  .regex(/^pk_(test|live)_[A-Za-z0-9]+$/, 'Not a valid Shift4 publishable key');

/** Apple Pay wallet config (merchant identifier + the per-env merchant-validation URL). */
const applePayConfigSchema = z.object({
  merchant_identifier: z.string().min(1),
  /** Backend endpoint that proxies Apple Pay merchant validation (already hardened). */
  merchant_validation_url: z.string().url(),
  supported_networks: z.array(z.string()).optional(),
});

/** Google Pay wallet config (gateway is fixed to `shift4payments`; merchant id is per-env). */
const googlePayConfigSchema = z.object({
  /** Google Pay merchant registration id (e.g. `BCR2DN4T6OF57KCN`). */
  merchant_id: z.string().min(1),
  merchant_name: z.string().min(1).optional(),
  /** Defaults to `'PRODUCTION'` for live keys, `'TEST'` otherwise when omitted. */
  environment: z.enum(['TEST', 'PRODUCTION']).optional(),
});

export const paymentConfigSchema = z.object({
  /** Active processor identifier. v1 is Shift4; F5 Rung 2 swaps this without a repo change. */
  processor: z.string().default('shift4'),
  publishable_key: publishableKeySchema,
  /** Per-env hosted card-page URLs (the WKWebView card surface), when served. */
  card_page_url: z.string().url().optional(),
  apple_pay: applePayConfigSchema.optional(),
  google_pay: googlePayConfigSchema.optional(),
});

export type PaymentConfig = z.infer<typeof paymentConfigSchema>;
export type ApplePayConfig = z.infer<typeof applePayConfigSchema>;
export type GooglePayConfig = z.infer<typeof googlePayConfigSchema>;

/**
 * Validate a raw config payload (e.g. a parsed JSON body) into a typed `PaymentConfig`.
 * Throws a `ZodError` on a malformed payload — the trust-boundary guard.
 */
export function parsePaymentConfig(raw: unknown): PaymentConfig {
  return paymentConfigSchema.parse(raw);
}

export interface FetchPaymentConfigOptions {
  /** The config endpoint URL (per-env; supplied by the consuming app, not baked here). */
  url: string;
  /** Optional fetch implementation override (defaults to global `fetch`). */
  fetchImpl?: typeof fetch;
  /** Forwarded to `fetch` — e.g. an App-Check'd RequestInit with `X-Firebase-AppCheck`. */
  init?: RequestInit;
  /** Abort the request after this many ms (config is off the critical path). Default 5000. */
  timeoutMs?: number;
}

/**
 * Fetch + validate the per-env payment config from the F5 endpoint.
 *
 * Returns the typed config, or throws on network failure, non-2xx, or a malformed payload.
 * Callers that want donate-web's best-effort posture (open the surface param-less, fall
 * back to the page's inline literal on ANY failure) should catch and degrade — this
 * function stays strict so the failure is observable rather than silently swallowed here.
 */
export async function fetchPaymentConfig(
  options: FetchPaymentConfigOptions,
): Promise<PaymentConfig> {
  const { url, fetchImpl = fetch, init, timeoutMs = 5000 } = options;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetchImpl(url, { ...init, signal: controller.signal });
    if (!response.ok) {
      throw new Error(`Payment config request failed: ${response.status}`);
    }
    const body: unknown = await response.json();
    return parsePaymentConfig(body);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Resolve the Google Pay environment from config or fall back to the publishable-key
 * prefix (matching the legacy pages' `_live_` detection). Centralized so every consumer
 * derives `'PRODUCTION'` vs `'TEST'` identically.
 */
export function resolveGooglePayEnvironment(
  config: Pick<PaymentConfig, 'publishable_key'> & {
    google_pay?: Pick<GooglePayConfig, 'environment'>;
  },
): 'TEST' | 'PRODUCTION' {
  if (config.google_pay?.environment) {
    return config.google_pay.environment;
  }
  return config.publishable_key.includes('_live_') ? 'PRODUCTION' : 'TEST';
}
